/**
 * Default Investment Costs — Heating System Installation
 *
 * Average installation costs for Polish market (2025-2026).
 * Includes equipment + installation labor, excluding chimney/ventilation modifications.
 *
 * Sources:
 * - instal-gaz.pl, muratordom.pl — equipment & installation prices
 * - Czyste Powietrze program — reference installation costs
 * - POBE — heating system cost benchmarks
 */

import type { FuelVariantId, InvestmentCostDefinition } from '../types/heatingCost.types';

export const DEFAULT_INVESTMENT_COSTS: Record<FuelVariantId, InvestmentCostDefinition> = {
  gas_old_boiler: {
    variantId: 'gas_old_boiler',
    installationCost: { min: 5000, max: 10000, typical: 7000 },
    annualMaintenanceCost: 500,
    expectedLifespan: 10,
    isUserModified: false,
  },
  gas_traditional_boiler: {
    variantId: 'gas_traditional_boiler',
    installationCost: { min: 6000, max: 14000, typical: 10000 },
    annualMaintenanceCost: 400,
    expectedLifespan: 15,
    isUserModified: false,
  },
  gas_condensing_boiler: {
    variantId: 'gas_condensing_boiler',
    installationCost: { min: 8000, max: 18000, typical: 12000 },
    annualMaintenanceCost: 400,
    expectedLifespan: 15,
    isUserModified: false,
  },
  lpg_condensing_boiler: {
    variantId: 'lpg_condensing_boiler',
    installationCost: { min: 12000, max: 25000, typical: 18000 },
    annualMaintenanceCost: 500,
    expectedLifespan: 15,
    isUserModified: false,
  },
  oil_traditional_boiler: {
    variantId: 'oil_traditional_boiler',
    installationCost: { min: 10000, max: 22000, typical: 15000 },
    annualMaintenanceCost: 600,
    expectedLifespan: 15,
    isUserModified: false,
  },
  oil_condensing_boiler: {
    variantId: 'oil_condensing_boiler',
    installationCost: { min: 12000, max: 25000, typical: 18000 },
    annualMaintenanceCost: 500,
    expectedLifespan: 15,
    isUserModified: false,
  },
  coal_stoker: {
    variantId: 'coal_stoker',
    installationCost: { min: 5000, max: 12000, typical: 8000 },
    annualMaintenanceCost: 300,
    expectedLifespan: 12,
    isUserModified: false,
  },
  coal_ecopea: {
    variantId: 'coal_ecopea',
    installationCost: { min: 8000, max: 16000, typical: 12000 },
    annualMaintenanceCost: 400,
    expectedLifespan: 12,
    isUserModified: false,
  },
  wood_log_boiler: {
    variantId: 'wood_log_boiler',
    installationCost: { min: 6000, max: 15000, typical: 10000 },
    annualMaintenanceCost: 300,
    expectedLifespan: 15,
    isUserModified: false,
  },
  wood_pellet_boiler: {
    variantId: 'wood_pellet_boiler',
    installationCost: { min: 12000, max: 28000, typical: 18000 },
    annualMaintenanceCost: 400,
    expectedLifespan: 15,
    isUserModified: false,
  },
  heat_pump_ground: {
    variantId: 'heat_pump_ground',
    installationCost: { min: 40000, max: 75000, typical: 55000 },
    annualMaintenanceCost: 500,
    expectedLifespan: 20,
    isUserModified: false,
  },
  heat_pump_air: {
    variantId: 'heat_pump_air',
    installationCost: { min: 25000, max: 55000, typical: 38000 },
    annualMaintenanceCost: 500,
    expectedLifespan: 15,
    isUserModified: false,
  },
  storage_heater: {
    variantId: 'storage_heater',
    installationCost: { min: 5000, max: 18000, typical: 10000 },
    annualMaintenanceCost: 100,
    expectedLifespan: 20,
    isUserModified: false,
  },
  electric_boiler: {
    variantId: 'electric_boiler',
    installationCost: { min: 3000, max: 10000, typical: 6000 },
    annualMaintenanceCost: 100,
    expectedLifespan: 15,
    isUserModified: false,
  },
  district_heating: {
    variantId: 'district_heating',
    installationCost: { min: 5000, max: 20000, typical: 10000 },
    annualMaintenanceCost: 200,
    expectedLifespan: 25,
    isUserModified: false,
  },
};

/** Get a copy of default investment costs */
export function getDefaultInvestmentCosts(): InvestmentCostDefinition[] {
  return Object.values(DEFAULT_INVESTMENT_COSTS).map((c) => ({
    ...c,
    installationCost: { ...c.installationCost },
  }));
}
